import {
  EmptyState,
  ErrorState,
  LoadingState,
  type Locale,
  WithHalfdot,
} from "@elyxeer/ui";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { VisibilityChip } from "~/components/content/VisibilityChip";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { fetchAdminDashboardHighlights } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { DashboardHighlightAdminSort } from "~/types/dashboard-highlights-admin";
import type { SortOrder } from "~/types/news-admin";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const TABLE_ICON_SIZE = 20;

type VisibilityFilter = "all" | "visible" | "hidden";

/** Current Dashboard Highlights plus hidden editorial history. */
export function DashboardHighlightsRoute() {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const [locale, setLocale] = useState<Locale | undefined>(undefined);
  const [visibility, setVisibility] = useState<VisibilityFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] =
    useState<DashboardHighlightAdminSort>("updated_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const isVisible = visibility === "all" ? undefined : visibility === "visible";
  const query = useInfiniteQuery({
    queryKey: queryKeys.dashboardHighlights({
      locale,
      search,
      isVisible,
      sortBy,
      sortOrder,
    }),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminDashboardHighlights({
        locale,
        search,
        isVisible,
        sortBy,
        sortOrder,
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const highlights = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const dateOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  const changeSort = (column: DashboardHighlightAdminSort) => {
    if (sortBy === column) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortOrder(column === "title" ? "asc" : "desc");
  };

  return (
    <Screen maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("highlights.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("highlights.subtitle")}
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/dashboard-highlights/new"
          variant="contained"
          startIcon={<AddIcon />}
          data-testid="highlights.new"
        >
          {t("highlights.newHighlight")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        <Tabs
          value={locale ?? "all"}
          onChange={(_event, value: string) =>
            setLocale(value === "all" ? undefined : (value as Locale))
          }
          sx={{ flex: 1, minWidth: 300 }}
        >
          <Tab label={t("highlights.filter.allLocales")} value="all" />
          <Tab label={t("highlights.filter.cs")} value="cs" />
          <Tab label={t("highlights.filter.en")} value="en" />
        </Tabs>

        <TextField
          select
          label={t("highlights.filter.visibility")}
          size="small"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityFilter)
          }
          slotProps={{
            htmlInput: { "data-testid": "highlights.filter-visibility" },
          }}
          sx={{ width: { xs: "100%", sm: 190 } }}
        >
          <MenuItem value="all">{t("highlights.filter.all")}</MenuItem>
          <MenuItem value="visible">{t("highlights.status.visible")}</MenuItem>
          <MenuItem value="hidden">{t("highlights.status.hidden")}</MenuItem>
        </TextField>

        <TextField
          label={t("highlights.search")}
          type="search"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: TABLE_ICON_SIZE }} />
                </InputAdornment>
              ),
            },
            htmlInput: { "data-testid": "highlights.search" },
          }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
      </Box>

      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : highlights.length === 0 ? (
        <EmptyState
          message={t(
            search || locale || visibility !== "all"
              ? "highlights.emptyFilter"
              : "highlights.empty",
          )}
        />
      ) : (
        <>
          <Typography
            variant="T3"
            sx={{ color: "text.muted", marginBottom: 1 }}
          >
            {t("highlights.count", { loaded: highlights.length, total })}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table data-testid="highlights.table" sx={{ minWidth: 840 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sortDirection={sortBy === "title" ? sortOrder : false}
                    sx={{ width: "45%" }}
                  >
                    <TableSortLabel
                      active={sortBy === "title"}
                      direction={sortBy === "title" ? sortOrder : "asc"}
                      onClick={() => changeSort("title")}
                      data-testid="highlights.sort-title"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("highlights.columns.title")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t("highlights.columns.locale")}</TableCell>
                  <TableCell>{t("highlights.columns.visibility")}</TableCell>
                  <TableCell
                    sortDirection={sortBy === "created_at" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "created_at"}
                      direction={sortBy === "created_at" ? sortOrder : "desc"}
                      onClick={() => changeSort("created_at")}
                      data-testid="highlights.sort-created-at"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("highlights.columns.createdAt")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "updated_at" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "updated_at"}
                      direction={sortBy === "updated_at" ? sortOrder : "desc"}
                      onClick={() => changeSort("updated_at")}
                      data-testid="highlights.sort-updated-at"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("highlights.columns.updatedAt")}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highlights.map((highlight) => (
                  <TableRow
                    key={highlight.id}
                    hover
                    onClick={() =>
                      void navigate(`/dashboard-highlights/${highlight.id}`)
                    }
                    data-testid={`highlights.row-${highlight.locale}-${highlight.slug}`}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="L1" sx={{ color: "text.primary" }}>
                        {highlight.title}
                      </Typography>
                      <Typography
                        variant="T3"
                        component="p"
                        sx={{ color: "text.muted" }}
                      >
                        {highlight.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={highlight.locale}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <VisibilityChip
                        visible={highlight.is_visible}
                        visibleLabel={t("highlights.status.visible")}
                        hiddenLabel={t("highlights.status.hidden")}
                      />
                    </TableCell>
                    <TableCell>
                      <time dateTime={highlight.created_at}>
                        {formatDate(highlight.created_at, dateOptions)}
                      </time>
                    </TableCell>
                    <TableCell>
                      <time dateTime={highlight.updated_at}>
                        {formatDate(highlight.updated_at, dateOptions)}
                      </time>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {query.hasNextPage ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <Button
                variant="outlined"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                data-testid="highlights.load-more"
              >
                {t("highlights.loadMore")}
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </Screen>
  );
}
